import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import { clearAllAiFlags, preparePriorAssessmentForNewDraft, sanitizeAiFlagsForEmptyAnswers } from '../utils/aiFlagHelpers';
import axios from 'axios';

import Role from '../Models/Role/Role.model';
import { ConsentForm } from '../Models/Consent/ConsentForm.model';
import { ConsentFormDocument } from '../Models/Consent/ConsentFormDocument.model';
import { MenuDetails } from '../Models/Assessment/AssessmentMenu.model';
import { copyObject, objectToFormData, removeEmptyDate } from '../utils/common';
import { isDisplayableDate } from '../utils/dateHelpers';
import { assessmentMenuEnum, assessmentStatusEnum, roleEnum, TeamTypes, TestingMachines } from '../utils/assessments';
import { MembersVitalSigns } from '../Models/VitalSigns/MembersVitalSigns.model';
import { AssessmentTypeEnum } from '../Enum/AssessmentTypeEnum';
import { MemberAssessment } from '../Models/Member/MemberAssessment.model';
import { AssessmentCategory } from '../Models/Assessment/AssessmentCategory.model';
import { SdohAssessment } from '../Models/Sdoh/SdohAssessment.model';
import { DiabetesFootAssessment } from '../Models/Assessment/DiabetesFootAssessment.model';
import { saveEngagementDraft, setEngagementDraft } from './documentEngagement.slice';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { MemberAllergy } from '../Models/Member/MemberAllergy.model';
import { Counseling } from '../Models/Assessment/Counseling.model';
import { MiniMentalExam } from '../Models/MiniMentalExams/MiniMentalExam.model';
import { SubjectiveAssessment } from '../Models/Assessment/SubjectiveAssessment.model';
import { FactorAndRiskAssessment } from '../Models/FactorAndRisk/FactorAndRiskAssessment.model';
import { getMemberBasicInfo, getMemberById, setMemberBasicInfo } from './memberDetail.slice';
import { MemberBasicInfo } from '../Models/Member/MemberBasicInfo.model';
import { HgbA1CAssessment } from '../Models/Assessment/HgbA1CAssessment.model';
import { Member } from '../Models/Member/Member.model';
import { LipidAssessment } from '../Models/Assessment/LipidAssessment.model';
import { MessageAlert } from '../Models/Shared/MessageAlert.model';
import { StatusEnum } from '../Enum/StatusEnum';
import {
    // EngagementDraft,
    ProviderRole,
} from '../Models/DocumentEngagements/EngagementDraft.model';
import { PrepareMenuClass } from '../Models/Shared/PrepareMenuClass.model';
import Authorization from '../utils/Authorization';
import { GenderEnum } from '../Enum/GenderEnum';
import { FactorRiskAssessmentLookupTypeEnum } from '../Enum/FactorRiskAssessmentLookupTypeEnum';
import { IntegratedCarePlan } from '../Models/CarePlans/IntegratedCarePlan.model';
import { ConsentDocumentValid } from '../Models/Consent/ConsentDocumentValid.model';
import { CPTcode } from '../Models/Shared/CPTCodeGroup';
import { insertTimeline } from './timeline.slice';
import { TimelineTypeEnum } from '../Enum/TimelineTypeEnum';
import { ICD10 } from '../Models/Shared/ICD10.model';
import { setShowMessage } from './shared.slice';
import { SuicidalHomicidalIdeations } from '../Models/SuicidalHomicidalIdeations/SuicidalHomicidalIdeations.model';
import { DocumentEngagement } from '../Models/DocumentEngagements/DocumentEngagement.model';

const initialValue = {
    assessmentType: AssessmentTypeEnum.PastPresentHistory,
    roles: [] as Role[],
    consentForm: new ConsentForm(),
    consentDocumentValid: [] as ConsentDocumentValid[],
    vitalSign: new MembersVitalSigns(),
    historyData: [] as MembersVitalSigns[],
    memberAssessment: new MemberAssessment(),
    memberAssessmentsByType: [] as MemberAssessment[],
    sdohAssessment: new SdohAssessment(),
    diabetesFootAssessment: new DiabetesFootAssessment(),
    counseling: new Counseling(),
    subjectiveAssessment: new SubjectiveAssessment(),
    miniMentalExam: new MiniMentalExam(),
    suicidalHomicidalIdeations: new SuicidalHomicidalIdeations(),
    memberAllergies: [] as MemberAllergy[],
    factorAndRiskAssessment: new FactorAndRiskAssessment(),
    hgbA1CAssessment: new HgbA1CAssessment(),
    lipidAssessment: new LipidAssessment(),
    deletingAlergyIds: [],
    fetchingConsentForm: false,
    fetchingVitalSign: false,
    fetchingSdoh: false,
    photoFile: null as File | null,
    photoFileName: '',
    showDeleteAlert: false,
    assessmentMenuList: [] as MenuDetails[],
    selectedRole: '',
    showUploadPhoto: false,
    isChanged: false,
    isSaving: false,
    isFetching: false,
    isDeleting: false,
    showAlleryModal: false,
    error: new MessageAlert(),
    success: new MessageAlert(),
    bhCPTCodeGroup: [] as CPTcode[],
    fCodesForBHDiagnostic: [] as ICD10[],
    showFCodeModal: false,
    fCodeCategory: '',
    baseCode: '',
    firstLevelOptions: [] as ICD10[],
    firstLevelSelection: '',
    secondLevelOptions: [] as ICD10[],
    secondLevelSelection: '',
    thirdLevelOptions: [] as ICD10[],
    thirdLevelSelection: '',
    pastPresentHistory: new MemberAssessment(),
    pastSurgicalHistory: new MemberAssessment(),
    reviewSystems: new MemberAssessment(),
    physicalExam: new MemberAssessment(),
};

const assessmentSlice = createSlice({
    name: 'assessment',
    initialState: initialValue,
    reducers: {
        _setAssessmentType(state, action) {
            state.assessmentType = action.payload;
        },
        _setBHCPTCodeGroup(state, action) {
            state.bhCPTCodeGroup = action.payload;
        },
        _setFCodesForBHDiagnostic(state, action) {
            state.fCodesForBHDiagnostic = action.payload;
        },
        _setShowFCodeModal(state, action) {
            state.showFCodeModal = action.payload;
        },
        _setFCodeCategory(state, action) {
            state.fCodeCategory = action.payload;
        },
        _setBaseCode(state, action) {
            state.baseCode = action.payload;
        },
        _setFirstLevelOptions(state, action) {
            state.firstLevelOptions = action.payload;
        },
        _setFirstLevelSelection(state, action) {
            state.firstLevelSelection = action.payload;
        },
        _setSecondLevelOptions(state, action) {
            state.secondLevelOptions = action.payload;
        },
        _setSecondLevelSelection(state, action) {
            state.secondLevelSelection = action.payload;
        },
        _setThirdLevelOptions(state, action) {
            state.thirdLevelOptions = action.payload;
        },
        _setThirdLevelSelection(state, action) {
            state.thirdLevelSelection = action.payload;
        },
        _setRoles(state, action) {
            state.roles = action.payload;
        },
        _setConsentForm(state, action) {
            state.consentForm = action.payload;
        },
        _setConsentDocumentValid(state, action) {
            state.consentDocumentValid = action.payload;
        },
        _setVitalSign(state, action) {
            state.vitalSign = action.payload;
        },
        _setVitalSignHistory(state, action) {
            state.historyData = action.payload;
        },
        _setMemberAssessment(state, action) {
            state.memberAssessment = action.payload;
            state.memberAssessmentsByType = [
                ...state.memberAssessmentsByType.filter((m) => m.Assessment?.AssessmentType !== state.memberAssessment?.Assessment?.AssessmentType),
                state.memberAssessment,
            ];
        },
        _setSdoh(state, action) {
            state.sdohAssessment = action.payload;
        },
        _setDiabetesFootAssessment(state, action) {
            state.diabetesFootAssessment = action.payload;
        },
        _setMemberAllergies(state, action) {
            state.memberAllergies = action.payload;
        },
        _setDeletingAlergyIds(state, action) {
            state.deletingAlergyIds = action.payload;
        },
        _setMiniMentalExam(state, action) {
            state.miniMentalExam = action.payload;
        },
        _setSuicidalHomicidalIdeations(state, action) {
            state.suicidalHomicidalIdeations = action.payload;
        },
        _setCounceling(state, action) {
            state.counseling = action.payload;
        },
        _setSubjectiveAssessment(state, action) {
            state.subjectiveAssessment = action.payload;
        },
        _setFactorAndRiskAssessment(state, action) {
            state.factorAndRiskAssessment = action.payload;
        },
        _setHgbA1CAssessment(state, action) {
            state.hgbA1CAssessment = action.payload;
        },
        _setIsChanged(state, action) {
            state.isChanged = action.payload;
        },
        _setLipidAssessment(state, action) {
            state.lipidAssessment = action.payload;
        },
        _setError(state, action) {
            state.error = action.payload;
        },
        _setPhotoFile(state, action) {
            state.photoFile = action.payload;
        },
        _setPhotoFileName(state, action) {
            state.photoFileName = action.payload;
        },
        _setShowUploadPhoto(state, action) {
            state.showUploadPhoto = action.payload;
        },
        _setSuccess(state, action) {
            state.success = action.payload;
        },
        _toggleFetchingConsentForm(state, action) {
            state.fetchingConsentForm = action.payload;
        },
        _toggleFetchingVitalSign(state, action) {
            state.fetchingVitalSign = action.payload;
        },
        _toggleFetchingSdoh(state, action) {
            state.fetchingSdoh = action.payload;
        },
        _toggleShowDeleteAlert(state, action) {
            state.showDeleteAlert = action.payload;
        },
        _updateAssessmentMenuList(state, action) {
            state.assessmentMenuList = action.payload;
        },
        _updateSelectedRole(state, action) {
            state.selectedRole = action.payload;
        },
        _toggleIsSaving(state, action) {
            state.isSaving = action.payload;
        },
        _toggleIsDeleting(state, action) {
            state.isDeleting = action.payload;
        },
        _toggleIsFetching(state, action) {
            state.isFetching = action.payload;
        },
        _toggleShowAlleryModal(state, action) {
            state.showAlleryModal = action.payload;
        },
        _setPastPresentHistory(state, action) {
            state.pastPresentHistory = action.payload;
        },
        _setPastSurgicalHistory(state, action) {
            state.pastSurgicalHistory = action.payload;
        },
        _setReviewSystems(state, action) {
            state.reviewSystems = action.payload;
        },
        _setPhysicalExam(state, action) {
            state.physicalExam = action.payload;
        },
        _resetAssessment(state, action) {
            state.consentForm = new ConsentForm();
            state.consentDocumentValid = [] as ConsentDocumentValid[];
            state.vitalSign = new MembersVitalSigns();
            state.historyData = [] as MembersVitalSigns[];
            state.sdohAssessment = new SdohAssessment();
            state.pastPresentHistory = new MemberAssessment();
            state.pastSurgicalHistory = new MemberAssessment();
            state.reviewSystems = new MemberAssessment();
            state.physicalExam = new MemberAssessment();
            state.diabetesFootAssessment = new DiabetesFootAssessment();
            state.counseling = new Counseling();
            state.subjectiveAssessment = new SubjectiveAssessment();
            state.miniMentalExam = new MiniMentalExam();
            state.suicidalHomicidalIdeations = new SuicidalHomicidalIdeations();
            state.memberAllergies = [] as MemberAllergy[];
            state.factorAndRiskAssessment = new FactorAndRiskAssessment();
            state.hgbA1CAssessment = new HgbA1CAssessment();
            state.lipidAssessment = new LipidAssessment();
            state.error = new MessageAlert();
            state.success = new MessageAlert();
            state.memberAssessment = new MemberAssessment();
            state.memberAssessmentsByType = [] as MemberAssessment[];
            state.assessmentMenuList = [] as MenuDetails[];
            state.deletingAlergyIds = [];
            state.fetchingConsentForm = false;
            state.fetchingVitalSign = false;
            state.fetchingSdoh = false;
            state.showDeleteAlert = false;
            state.isSaving = false;
            state.isFetching = false;
            state.isDeleting = false;
            state.showAlleryModal = false;
            state.error = new MessageAlert();
            state.success = new MessageAlert();
        },
    },
});

const {
    _setAssessmentType,
    _setBHCPTCodeGroup,
    _setFCodesForBHDiagnostic,
    _setShowFCodeModal,
    _setFCodeCategory,
    _setBaseCode,
    _setFirstLevelOptions,
    _setFirstLevelSelection,
    _setSecondLevelOptions,
    _setSecondLevelSelection,
    _setThirdLevelOptions,
    _setThirdLevelSelection,
    _setRoles,
    _setConsentForm,
    _setConsentDocumentValid,
    _setVitalSign,
    _setVitalSignHistory,
    _setMemberAssessment,
    _setSdoh,
    _setDiabetesFootAssessment,
    _setMemberAllergies,
    _setDeletingAlergyIds,
    _setMiniMentalExam,
    _setSuicidalHomicidalIdeations,
    _setCounceling,
    _setSubjectiveAssessment,
    _setFactorAndRiskAssessment,
    _setHgbA1CAssessment,
    _setIsChanged,
    _setLipidAssessment,
    _setError,
    _setPhotoFile,
    _setPhotoFileName,
    _setShowUploadPhoto,
    _setSuccess,
    _toggleFetchingConsentForm,
    _toggleFetchingVitalSign,
    _toggleFetchingSdoh,
    _toggleShowDeleteAlert,
    _toggleIsSaving,
    _toggleIsFetching,
    _toggleIsDeleting,
    _updateAssessmentMenuList,
    _updateSelectedRole,
    _toggleShowAlleryModal,
    _setPastPresentHistory,
    _setPastSurgicalHistory,
    _setReviewSystems,
    _setPhysicalExam,
    _resetAssessment,
} = assessmentSlice.actions;

export const resetAssessment = (): AppThunk => (dispatch, getState) => {
    dispatch(_resetAssessment(''));
};

export const setAssessmentType =
    (assessmentType: AssessmentTypeEnum): AppThunk =>
    async (dispatch) => {
        dispatch(_setAssessmentType(assessmentType));
    };

export const setMemberAssessment =
    (memberAssessment: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setMemberAssessment(memberAssessment));
    };

export const fetchRoles = (): AppThunk => async (dispatch, getState) => {
    const response = await axios.get(`${API_URL}/role/roles`);
    dispatch(_setRoles(response.data));
};

export const setFCodesForBHDiagnostic =
    (fCodesForBHDiagnostic: ICD10[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setFCodesForBHDiagnostic(fCodesForBHDiagnostic));
    };

export const setShowFCodeModal =
    (show: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setShowFCodeModal(show));
    };

export const setFCodeCategory =
    (category: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setFCodeCategory(category));
    };

export const setShowUploadPhoto =
    (show: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setShowUploadPhoto(show));
    };

export const setBaseCode =
    (baseCode: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setBaseCode(baseCode));
    };

export const setFirstLevelOptions =
    (firstLevelOptions: ICD10[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setFirstLevelOptions(firstLevelOptions));
    };

export const setFirstLevelSelection =
    (firstLevelSelection: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setFirstLevelSelection(firstLevelSelection));
    };

export const setIsChanged =
    (isChanged: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsChanged(isChanged));
    };

export const setPhotoFile =
    (photoFile: File | null): AppThunk =>
    async (dispatch) => {
        dispatch(_setPhotoFile(photoFile));
    };

export const setPhotoFileName =
    (photoFileName: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setPhotoFileName(photoFileName));
    };

export const setSecondLevelOptions =
    (secondLevelOptions: ICD10[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setSecondLevelOptions(secondLevelOptions));
    };

export const setSecondLevelSelection =
    (secondLevelSelection: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setSecondLevelSelection(secondLevelSelection));
    };

export const setThirdLevelOptions =
    (thirdLevelOptions: ICD10[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setThirdLevelOptions(thirdLevelOptions));
    };

export const setSuicidalHomicidalIdeations =
    (suicidalHomicidalIdeations: SuicidalHomicidalIdeations): AppThunk =>
    async (dispatch) => {
        dispatch(_setSuicidalHomicidalIdeations(suicidalHomicidalIdeations));
    };

export const setThirdLevelSelection =
    (thirdLevelSelection: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setThirdLevelSelection(thirdLevelSelection));
    };

export const setPastPresentHistory =
    (pastPresentHistory: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setPastPresentHistory(pastPresentHistory));
    };

export const setPastSurgicalHistory =
    (pastSurgicalHistory: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setPastSurgicalHistory(pastSurgicalHistory));
    };

export const setReviewSystems =
    (reviewSystems: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setReviewSystems(reviewSystems));
    };

export const setPhysicalExam =
    (physicalExam: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setPhysicalExam(physicalExam));
    };

export const setVitalSign =
    (vitalSign: MembersVitalSigns): AppThunk =>
    async (dispatch) => {
        dispatch(_setVitalSign(vitalSign));
    };

export const setVitalSignHistory =
    (historyData: MembersVitalSigns[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setVitalSignHistory(historyData));
    };

export const handleOpenUploadPhoto = (): AppThunk => async (dispatch) => {
    dispatch(setShowUploadPhoto(true));
};

export const uploadSkinPhoto =
    (file: File, memberId: string): AppThunk =>
    async (dispatch, getState) => {
        try {
            dispatch(_toggleIsSaving(true));
            const fileName = getState().assessment.photoFileName;
            const url = window.URL.createObjectURL(file);
            const formData = new FormData();
            formData.append('memberId', memberId);
            formData.append('fileName', fileName);
            formData.append('fileUrl', url);
            formData.append('skinPhoto', file, file.name);
            await axios.post(`${API_URL}/document/uploadskinphoto`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            dispatch(_toggleIsSaving(false));
            dispatch(setShowMessage(true, 'Image uploaded successfully', 'success'));
        } catch (error) {
            dispatch(_toggleIsSaving(false));
            dispatch(setShowMessage(true, 'Failed to upload image', 'error'));
        }
    };

export const saveMemberConsentForm =
    (data: ConsentForm, isAssessmentWorkflow: boolean, extraDraftData = {}): AppThunk<Promise<void>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        let formData = new FormData();
        if (Boolean(data.PartOneConsent) && Boolean(data.PartOneConsent.File)) {
            formData.append('part1Consent', data.PartOneConsent.File, data.PartOneConsent.FileName);
        }

        if (Boolean(data.PartTwoConsent) && Boolean(data.PartTwoConsent.File)) {
            formData.append('part2Consent', data.PartTwoConsent.File, data.PartTwoConsent.FileName);
        }

        let copyData = copyObject(data);
        formData.append('MemberId', copyData.MemberId);

        if (Boolean(copyData.PartOneConsent?.File)) {
            copyData.PartOneConsent.MemberId = copyData.MemberId;
        }
        if (Boolean(copyData.PartTwoConsent?.File)) {
            copyData.PartTwoConsent.MemberId = copyData.MemberId;
        }
        objectToFormData(copyData, formData, 'consentForm');
        const response = await axios.put(`${API_URL}/document/insertOrUpdateMemberConsentForm`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        dispatch(_setConsentForm(response.data));
        if (isAssessmentWorkflow) {
            const menuList = getState().assessment.assessmentMenuList;
            let nextMenuIndex;
            if (data.ConsentToService) {
                nextMenuIndex = menuList.findIndex((m) => m.IsActive) + 1;
            } else {
                nextMenuIndex = menuList.findIndex((m) => m.Name === assessmentMenuEnum.SET_APPOINTMENTS);
            }
            const nextMenu = menuList[nextMenuIndex];

            await dispatch(
                prepareNewConsentEngagementDraft(response.data, data.MemberId, {
                    ...extraDraftData,
                    CurrentMenu: nextMenu?.Name || '',
                })
            );
            dispatch(getConsentFormDocumentByMember(data.MemberId));
        }
        dispatch(_toggleIsSaving(false));
    };

const prepareNewConsentEngagementDraft =
    (consentForm: ConsentForm, memberId: string, extraDraftData = {}): AppThunk<Promise<void>> =>
    async (dispatch, getState) => {
        let engagementDraft = copyObject(getState().documentEngagement.engagementDraft || {});
        let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
        let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.ConsentForm);
        let summaryNote = {
            Id: consentForm.Id,
            CptCode: '',
            Zcode: '',
            Note: 'Completed Consent Form',
            Type: SummaryNoteTypeEnum.ConsentForm,
            IsConfirm: true,
            OrderBy: 30,
            DisplayName: 'Consent Form',
        };
        if (summaryNote.Id != null) {
            summaryNotes.push(summaryNote);
        }
        if (!Boolean(engagementDraft.MeansOfEngagement)) {
            delete engagementDraft.MeansOfEngagement;
        }

        engagementDraft.MemberId = memberId;
        engagementDraft.ConsentFormId = consentForm.Id;
        engagementDraft.SummaryNotes = summaryNotes;
        engagementDraft = { ...engagementDraft, ...extraDraftData };
        const assessmentSlice = getState().assessment;
        const selectedRole = assessmentSlice.roles.find((r) => r.Name === assessmentSlice.selectedRole);
        engagementDraft.AssessedRole = {
            Id: selectedRole.Id,
            RoleName: selectedRole.Name,
        } as ProviderRole;
        const updatedMemberAssessmentStatuses = {
            ...memberAssessmentHistory.MemberAssessmentStatuses,
            IsConsentToService: consentForm.ConsentToService,
        };
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: engagementDraft,
            MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
        };
        await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        dispatch(
            updateAssessmentMenu({
                ActiveMenu: engagementDraft.CurrentMenu || '',
                EngagementDraft: engagementDraft,
            } as PrepareMenuClass)
        );
    };

export const getMemberConsentForm =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleFetchingConsentForm(true));
        const response = await axios.get(`${API_URL}/document/getmemberconsentform?id=${id}`);
        if (response.data.PartOneConsent && response.data.PartOneConsent.FileName) {
            response.data.PartOneConsent.IsDocumentPreview = true;
        } else {
            response.data.PartOneConsent = new ConsentFormDocument();
        }

        if (response.data.PartTwoConsent && response.data.PartTwoConsent.FileName) {
            response.data.PartTwoConsent.IsDocumentPreview = true;
        } else {
            response.data.PartTwoConsent = new ConsentFormDocument();
        }

        response.data = removeEmptyDate(response.data);
        dispatch(_setConsentForm(response.data));
        dispatch(_toggleFetchingConsentForm(false));
    };

export const getConsentFormDocumentByMember =
    (memberId: string): AppThunk =>
    (dispatch, getState) => {
        axios
            .get(`${API_URL}/document/getconsentformbymemberid`, {
                params: { memberId },
            })
            .then((response) => {
                let consentFormDocs = response.data as ConsentDocumentValid[];
                dispatch(_setConsentDocumentValid(consentFormDocs));
            })
            .catch((err) => {
                dispatch(_setConsentDocumentValid([] as ConsentDocumentValid[]));
            });
    };

export const deleteConsentDocument =
    (documentId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsDeleting(true));
        const response = await axios.delete(`${API_URL}/document/deletedocument?documentId=` + documentId);
        if (response.status === 200) {
            let consentForm = getState().assessment?.consentForm;
            if (consentForm.PartOneConsent.Id === documentId) {
                consentForm = {
                    ...consentForm,
                    PartOneConsent: new ConsentFormDocument(),
                };
            } else {
                consentForm = {
                    ...consentForm,
                    PartTwoConsent: new ConsentFormDocument(),
                };
            }

            dispatch(_setConsentForm(consentForm));
        }
        dispatch(_toggleShowDeleteAlert(false));
        dispatch(_toggleIsDeleting(false));
    };

export const toggleShowDeleteAlert =
    (show: boolean): AppThunk =>
    (dispatch, getState) => {
        dispatch(_toggleShowDeleteAlert(show));
    };

export const gotoNextMenu = (): AppThunk => async (dispatch, getState) => {
    const menuList = getState().assessment.assessmentMenuList;
    const nextMenuIndex = menuList.findIndex((m) => m.IsActive) + 1;
    if (menuList.length >= nextMenuIndex + 1) {
        setTimeout(() => {
            dispatch(
                updateAssessmentMenu({
                    ActiveMenu: menuList[nextMenuIndex].Name,
                } as PrepareMenuClass)
            );
        }, 0);
    }
};

export const gotoPreviousMenu = (): AppThunk => async (dispatch, getState) => {
    const menuList = getState().assessment.assessmentMenuList.filter((a) => !a.IsDisabled);
    const previousMenuIndex = menuList.findIndex((m) => m.IsActive) - 1;
    if (menuList.length >= previousMenuIndex + 1) {
        dispatch(
            updateAssessmentMenu({
                ActiveMenu: menuList[previousMenuIndex].Name,
            } as PrepareMenuClass)
        );
    }
};

export const fetchMemberVitalSign =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleFetchingVitalSign(true));
        const response = await axios.get(`${API_URL}/vitalsigns/getmembervitalsignsbyid?id=${id}`);
        dispatch(_setVitalSign(response.data));
        dispatch(_toggleFetchingVitalSign(false));
    };

export const fetchVitalSignByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleFetchingVitalSign(true));
        const response = await axios.get(`${API_URL}/vitalsigns/getvitalsignsbymemberid?memberId=${memberId}`);
        if (response.data) {
            preparePriorAssessmentForNewDraft(response.data, { clearStatus: true });
        }
        dispatch(_setVitalSign(response.data));
        dispatch(_toggleFetchingVitalSign(false));
    };

export const fetchVitalSignHistory =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/vitalsigns/getvitalsignhistory/${memberId}`);
        dispatch(_setVitalSignHistory(response.data));
    };

export const saveMemberVitalsign =
    (memberVitalSigns: MembersVitalSigns, isWithoutComplete = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            const response = await axios.put(`${API_URL}/vitalsigns/insertmembervitalsigns`, memberVitalSigns);
            dispatch(_setVitalSign(response.data));
            const isDraftSaved = await dispatch(prepareVitalSignSummaryNotes(response.data, isWithoutComplete));
            if (isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

export const setSelectedRole =
    (role: string): AppThunk =>
    async (dispatch) => {
        dispatch(_updateSelectedRole(role));
    };

export const updateMemberVitalSign =
    (memberVitalSigns: MembersVitalSigns, isWithoutComplete = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            await axios.put(`${API_URL}/vitalsigns/updatemembervitalsigns`, memberVitalSigns);
            dispatch(_setVitalSign(memberVitalSigns));
            const isDraftSaved = await dispatch(prepareVitalSignSummaryNotes(memberVitalSigns, isWithoutComplete));
            if (isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

const prepareVitalSignSummaryNotes =
    (memberVitalSigns: MembersVitalSigns, isWithoutComplete = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        let engagementDraft = copyObject(getState().documentEngagement.engagementDraft || {});
        let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
        let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.VitalSign);
        const bmi = parseFloat(memberVitalSigns.BMI);
        if (bmi > 0) {
            let summaryNote = null;
            if (bmi <= 19.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.1',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 20 && bmi <= 20.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.20',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 21 && bmi <= 21.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.21',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 22 && bmi <= 22.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.22',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 23 && bmi <= 23.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.23',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 24 && bmi <= 24.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.24',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 25 && bmi <= 25.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.25',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 26 && bmi <= 26.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.26',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 27 && bmi <= 27.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.27',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 28 && bmi <= 28.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.28',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 29 && bmi <= 29.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.29',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 30 && bmi <= 30.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.30',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 31 && bmi <= 31.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.31',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 32 && bmi <= 32.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.32',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 33 && bmi <= 33.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.33',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 34 && bmi <= 34.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.34',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 35 && bmi <= 35.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.35',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 36 && bmi <= 36.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.36',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 37 && bmi <= 37.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.37',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 38 && bmi <= 38.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.38',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 39 && bmi <= 39.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.39',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 40 && bmi <= 44.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.41',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 45 && bmi <= 49.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.42',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 50 && bmi <= 59.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.43',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 60 && bmi <= 69.99) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.44',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bmi >= 70) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3008F',
                    Zcode: 'Z68.45',
                    Note: `Body mass index BMI - ${bmi}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            }
            if (Boolean(summaryNote?.Id)) {
                summaryNotes.push(summaryNote);
            }
        }

        // For BP systolic
        const bpSystolic = parseFloat(memberVitalSigns.BpSystolic);
        if (bpSystolic > 0) {
            let summaryNote = {};
            if (bpSystolic < 130) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3074F',
                    Zcode: '',
                    Note: `BP systolic - ${bpSystolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bpSystolic >= 130 && bpSystolic <= 139) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3075F',
                    Zcode: '',
                    Note: `BP systolic - ${bpSystolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            } else if (bpSystolic >= 140) {
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3077F',
                    Zcode: '',
                    Note: `BP systolic - ${bpSystolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            }
            summaryNotes.push(summaryNote);
        }

        // For BP diastolic
        const bpDiastolic = parseFloat(memberVitalSigns.BpDiastolic);
        if (bpDiastolic > 0) {
            let summaryNote = {};
            if (bpDiastolic < 80)
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3078F',
                    Zcode: '',
                    Note: `BP diastolic - ${bpDiastolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            else if (bpDiastolic >= 80 && bpDiastolic <= 89)
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3079F',
                    Zcode: '',
                    Note: `BP diastolic - ${bpDiastolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            else if (bpDiastolic >= 90)
                summaryNote = {
                    Id: memberVitalSigns.Id,
                    CptCode: '3080F',
                    Zcode: '',
                    Note: `BP diastolic - ${bpDiastolic}`,
                    Type: SummaryNoteTypeEnum.VitalSign,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Vital Sign',
                };
            summaryNotes.push(summaryNote);
        }

        // For glucose
        const glucoseMgDl = parseFloat(memberVitalSigns.GlucoseMgDl);
        if (glucoseMgDl > 0) {
            let summaryNote = {
                Id: memberVitalSigns.Id,
                CptCode: '82962',
                Zcode: '',
                Note: `Glucose - ${glucoseMgDl}`,
                Type: SummaryNoteTypeEnum.VitalSign,
                IsConfirm: true,
                OrderBy: 1,
                DisplayName: 'Vital Sign',
            };
            summaryNotes.push(summaryNote);
        }

        if (isWithoutComplete) {
            engagementDraft.VitalSignStatus = assessmentStatusEnum.COMPLETED;
        }
        const updatedEngagementDraft = {
            ...engagementDraft,
            SummaryNotes: summaryNotes,
            VitalsignAssessmentId: memberVitalSigns.Id,
        };
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: updatedEngagementDraft,
        };
        return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
    };

export const getPastPresentHistory =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraft = getState().documentEngagement.engagementDraft;
        const id = getAssessmentIdByType(AssessmentTypeEnum.PastPresentHistory, engagementDraft);
        if (Boolean(id)) {
            dispatch(getMemberAssessmentById(id, (data) => dispatch(setPastPresentHistory(data))));
        } else {
            dispatch(_toggleIsFetching(true));
            const response = await axios.get(`${API_URL}/assessment/getmemberassessment/${AssessmentTypeEnum.PastPresentHistory}?memberId=${memberId}`);
            const pastPresentHistoryData = copyObject(response.data);
            pastPresentHistoryData.Id = null;
            clearAllAiFlags(pastPresentHistoryData);
            dispatch(setPastPresentHistory(pastPresentHistoryData));
            dispatch(_toggleIsFetching(false));
        }
    };

export const getPastSurgicalHistory =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraft = getState().documentEngagement.engagementDraft;
        const id = getAssessmentIdByType(AssessmentTypeEnum.PastSurgicalHistory, engagementDraft);
        if (Boolean(id)) {
            dispatch(getMemberAssessmentById(id, (data) => dispatch(setPastSurgicalHistory(data))));
        } else {
            dispatch(_toggleIsFetching(true));
            const response = await axios.get(`${API_URL}/assessment/getmemberassessment/${AssessmentTypeEnum.PastSurgicalHistory}?memberId=${memberId}`);
            const pastSurgicalHistoryData = copyObject(response.data);
            pastSurgicalHistoryData.Id = null;
            clearAllAiFlags(pastSurgicalHistoryData);
            dispatch(setPastSurgicalHistory(pastSurgicalHistoryData));
            dispatch(_toggleIsFetching(false));
        }
    };

export const getReviewSystems =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraft = getState().documentEngagement.engagementDraft;
        const id = getAssessmentIdByType(AssessmentTypeEnum.ReviewSystems, engagementDraft);
        if (Boolean(id)) {
            dispatch(getMemberAssessmentById(id, (data) => dispatch(setReviewSystems(data))));
        } else {
            dispatch(_toggleIsFetching(true));
            const response = await axios.get(`${API_URL}/assessment/getmemberassessment/${AssessmentTypeEnum.ReviewSystems}?memberId=${memberId}`);
            const reviewSystemsData = copyObject(response.data);
            reviewSystemsData.Id = null;
            clearAllAiFlags(reviewSystemsData);
            dispatch(setReviewSystems(reviewSystemsData));
            dispatch(_toggleIsFetching(false));
        }
    };

export const getPhysicalExam =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraft = getState().documentEngagement.engagementDraft;
        const id = getAssessmentIdByType(AssessmentTypeEnum.PhysicalExam, engagementDraft);
        if (Boolean(id)) {
            dispatch(getMemberAssessmentById(id, (data) => dispatch(setPhysicalExam(data))));
        } else {
            dispatch(_toggleIsFetching(true));
            const response = await axios.get(`${API_URL}/assessment/getmemberassessment/${AssessmentTypeEnum.PhysicalExam}?memberId=${memberId}`);
            const physicalExamData = copyObject(response.data);
            physicalExamData.Id = null;
            clearAllAiFlags(physicalExamData);
            dispatch(setPhysicalExam(physicalExamData));
            dispatch(_toggleIsFetching(false));
        }
    };

export const getAssessmentIdByType = (type: AssessmentTypeEnum, engagementDraft: DocumentEngagement) => {
    let id = null;
    if (engagementDraft) {
        switch (type) {
            case AssessmentTypeEnum.PastPresentHistory:
                id = engagementDraft.PastPresentHistoryId;
                break;
            case AssessmentTypeEnum.PastSurgicalHistory:
                id = engagementDraft.PastSurgicalHistoryId;
                break;
            case AssessmentTypeEnum.SdohAssessment:
                id = engagementDraft.SdohAssessmentId;
                break;
            case AssessmentTypeEnum.ReviewSystems:
                id = engagementDraft.ReviewSystemId;
                break;
            case AssessmentTypeEnum.PhysicalExam:
                id = engagementDraft.PhysicalExamId;
                break;
            case AssessmentTypeEnum.UrinalysisReport:
                id = engagementDraft.UrinalysisReportId;
                break;
            case AssessmentTypeEnum.DiabetesFootAssessment:
                id = engagementDraft.DiabetesFootAssessmentId;
                break;
            case AssessmentTypeEnum.PatientHealthQuestionnaire:
                id = engagementDraft.PhqAssessmentId;
                break;
            case AssessmentTypeEnum.HamiltonAnxietyScale:
                id = engagementDraft.HamiltonAnxietyScaleId;
                break;
            case AssessmentTypeEnum.LegalHistory:
                id = engagementDraft.LegalHistoryId;
                break;
        }
    }
    return id;
};

export const getAssessmentByType =
    (type: AssessmentTypeEnum, memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraft = getState().documentEngagement.engagementDraft;
        const id = getAssessmentIdByType(type, engagementDraft);
        if (Boolean(id)) {
            dispatch(getMemberAssessmentById(id));
        } else {
            dispatch(_toggleIsFetching(true));
            const response = await axios.get(`${API_URL}/assessment/getmemberassessment/${type}?memberId=${memberId}`);
            response.data.Id = null;
            clearAllAiFlags(response.data);
            dispatch(_setMemberAssessment(response.data));
            dispatch(_toggleIsFetching(false));
        }
    };

export const getMemberAssessmentById =
    (id: string, callBack: ((data: any) => void) | null = null): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        axios
            .get(`${API_URL}/assessment/getmemberassessmentbyid/${id}`)
            .then((response) => {
                if (response.data) {
                    sanitizeAiFlagsForEmptyAnswers(response.data);
                }
                if (callBack) {
                    callBack(response.data);
                } else {
                    dispatch(_setMemberAssessment(response.data));
                }
            })
            .catch((err) => {
                dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            })
            .finally(() => {
                dispatch(_toggleIsFetching(false));
            });
    };

export const handleOnChangeCategory =
    (updatedCategory: AssessmentCategory): AppThunk =>
    async (dispatch, getState) => {
        console.log('handleChangeCategory: ', updatedCategory);
        if (!updatedCategory) {
            console.warn('Updated category is null or undefined');
            return;
        }

        // Add additional validation
        if (!updatedCategory.Questions) {
            console.warn('Updated category has no questions array');
            return;
        }

        try {
            dispatch(setIsChanged(true));
            dispatch(updateMemberAssessmentModel(updatedCategory));
        } catch (error) {
            console.error('Error in handleOnChangeCategory:', error);
            console.error('Category data:', updatedCategory);
        }
    };

export const updateMemberAssessmentModel =
    (category: AssessmentCategory): AppThunk =>
    async (dispatch, getState) => {
        let memberAssessment = copyObject(getState().assessment.memberAssessment);
        memberAssessment.Assessment.Categories = memberAssessment.Assessment.Categories.map((cate) => {
            if (category.Id === cate.Id) {
                return category;
            }

            return cate;
        });
        dispatch(_setMemberAssessment(memberAssessment));
    };

export const saveAssessmentWorkflow =
    (category: AssessmentCategory, isWithoutComplete = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (gotoNext) {
            dispatch(_toggleIsSaving(true));
        }
        let memberAssessment = copyObject(getState().assessment.memberAssessment);
        if (isWithoutComplete) {
            memberAssessment.Status = StatusEnum.Completed;
        }
        memberAssessment.Assessment.Categories = memberAssessment.Assessment.Categories.map((cate) => {
            if (category.Id === cate.Id) {
                return category;
            }

            return cate;
        });

        dispatch(_setMemberAssessment(memberAssessment));
        const response = await axios.put(`${API_URL}/assessment/createorupdateassessmentworkflow`, memberAssessment);
        dispatch(_setMemberAssessment(response.data));
        dispatch(_toggleIsFetching(false));

        let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
        switch (memberAssessment.Assessment.AssessmentType) {
            case AssessmentTypeEnum.PastPresentHistory:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.PastPresentHistory) || [];
                engagementDraft.SummaryNotes.push({
                    Id: response.data.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: 'Assessed Past Present History',
                    Type: SummaryNoteTypeEnum.PastPresentHistory,
                    IsConfirm: true,
                    OrderBy: 4,
                    DisplayName: 'Past Present History',
                });

                engagementDraft.PastPresentHistoryId = response.data.Id;
                break;
            case AssessmentTypeEnum.PastSurgicalHistory:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.PastSurgicalHistory) || [];
                engagementDraft.SummaryNotes.push({
                    Id: response.data.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: 'Assessed Past Surgical History',
                    Type: SummaryNoteTypeEnum.PastSurgicalHistory,
                    IsConfirm: true,
                    OrderBy: 4,
                    DisplayName: 'Past Surgical History',
                });

                engagementDraft.PastSurgicalHistoryId = response.data.Id;
                break;
            case AssessmentTypeEnum.ReviewSystems:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.ReviewSystems) || [];
                engagementDraft.SummaryNotes.push({
                    Id: response.data.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: 'Assessed Review Systems',
                    Type: SummaryNoteTypeEnum.ReviewSystems,
                    IsConfirm: true,
                    OrderBy: 4,
                    DisplayName: 'Assessed Review Systems',
                });

                engagementDraft.ReviewSystemId = response.data.Id;
                break;
            case AssessmentTypeEnum.PhysicalExam:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.PhysicalExam) || [];
                engagementDraft.SummaryNotes.push({
                    Id: response.data.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: 'Assessed Physical Exam',
                    Type: SummaryNoteTypeEnum.PhysicalExam,
                    IsConfirm: true,
                    OrderBy: 4,
                    DisplayName: 'Physical Exam',
                });

                engagementDraft.PhysicalExamId = response.data.Id;
                break;
            case AssessmentTypeEnum.UrinalysisReport:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.UrinalysisReport) || [];
                engagementDraft.SummaryNotes.push({
                    Id: response.data.Id,
                    CptCode: '81002',
                    Zcode: '',
                    Note: 'Assessed Urinalysis Report',
                    Type: SummaryNoteTypeEnum.UrinalysisReport,
                    IsConfirm: true,
                    OrderBy: 4,
                    DisplayName: 'Assessed Urinalysis Report',
                });

                engagementDraft.UrinalysisReportId = response.data.Id;
                break;
            case AssessmentTypeEnum.DiabetesFootAssessment:
                engagementDraft.DiabetesFootAssessmentId = response.data.Id;
                break;
            case AssessmentTypeEnum.PatientHealthQuestionnaire:
                engagementDraft.SummaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.PHQ9) || [];
                let total = 0;
                const category = memberAssessment?.Assessment?.Categories[0];
                const firstQuestions = category?.Questions?.slice(0, category?.Questions.length - 1);
                firstQuestions.forEach((question) => {
                    total += question?.Options?.find((o) => o.IsSelected)?.Score || 0;
                });

                if (total > 14 && total <= 23) {
                    engagementDraft.SummaryNotes.push({
                        Id: response.data.Id,
                        CptCode: '',
                        Zcode: '',
                        Note: 'PHQ9 score is greater than 14 - Consider Antidepressant',
                        Type: SummaryNoteTypeEnum.PHQ9,
                        IsConfirm: true,
                        OrderBy: 8,
                        DisplayName: 'PHQ9',
                    });
                }
                if (total > 23) {
                    engagementDraft.SummaryNotes.push({
                        Id: response.data.Id,
                        CptCode: '',
                        Zcode: '',
                        Note: 'PHQ9 score is greater than 23 - Alert to BH',
                        Type: SummaryNoteTypeEnum.PHQ9,
                        IsConfirm: true,
                        OrderBy: 8,
                        DisplayName: 'PHQ9',
                    });
                }
                engagementDraft.PhqAssessmentId = response.data.Id;
                break;
        }
        let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: engagementDraft,
        };
        const isDraftSaved = await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        if (gotoNext) {
            if (isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            dispatch(_toggleIsSaving(false));
        }
        return isDraftSaved;
    };

export const getLastSdohAssessment =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleFetchingSdoh(true));
        axios
            .get(`${API_URL}/assessment/getlastsdohassessment`, { params: { memberId } })
            .then((response) => {
                if (Boolean(response.data)) {
                    preparePriorAssessmentForNewDraft(response.data);
                    response.data.IsLast = true;
                    dispatch(_setSdoh(response.data));
                }
            })
            .finally(() => {
                dispatch(_toggleFetchingSdoh(false));
            });
    };

export const fetchSdohAssessment =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleFetchingSdoh(true));
        const response = await axios.get(`${API_URL}/assessment/getsdohassessmentbyid?id=${id}`);
        sanitizeAiFlagsForEmptyAnswers(response.data);
        dispatch(_setSdoh(response.data));
        dispatch(_toggleFetchingSdoh(false));
    };

export const saveSdohDemographicsInfo =
    (memberBasicInfo: MemberBasicInfo): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        const response = await axios.put(`${API_URL}/assessment/savesdohdemographicsinfo`, memberBasicInfo);
        dispatch(setMemberBasicInfo(response.data));
        dispatch(_toggleIsSaving(false));
    };

export const saveSdohAssessment =
    (sdohAssessment: SdohAssessment): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            const response = await axios.put(`${API_URL}/assessment/createsdohassessment`, sdohAssessment);
            dispatch(_setSdoh(response.data));
            return await dispatch(addSdohSummary({ ...sdohAssessment, Id: response.data.Id }));
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

export const updateSdohAssessment =
    (sdohAssessment: SdohAssessment): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            await axios.put(`${API_URL}/assessment/updatesdohassessment`, sdohAssessment);
            dispatch(_setSdoh(sdohAssessment));
            return await dispatch(addSdohSummary(sdohAssessment));
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

export const prepareSdohRequest =
    (sdohAssessment: SdohAssessment): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            await axios.put(`${API_URL}/assessment/preparesdohrequest`, sdohAssessment);
        } catch (error) {}
        dispatch(_toggleIsSaving(false));
    };

export const addSdohSummary =
    (sdohAssessment: SdohAssessment): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
        let summaryNotes = engagementDraft.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.SDOH) || [];
        if (
            sdohAssessment.PersonalCharacteristics.HaveProblemsInReadingHealthLiterature &&
            sdohAssessment.PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.Answer != null &&
            sdohAssessment.PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.Answer.includes('Yes')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z55.6',
                Note: 'Problems in reading health literature',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.PersonalCharacteristics.DoYouHaveProblemsReading &&
            sdohAssessment.PersonalCharacteristics.DoYouHaveProblemsReading.Answer != null &&
            sdohAssessment.PersonalCharacteristics.DoYouHaveProblemsReading.Answer.includes('Yes')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z55.0',
                Note: 'Illiteracy and low-level literacy',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.FamilyAndHome.HousingSituationToday &&
            sdohAssessment.FamilyAndHome.HousingSituationToday.Answer != null &&
            sdohAssessment.FamilyAndHome.HousingSituationToday.Answer.includes('I do not have housing') &&
            sdohAssessment.FamilyAndHome.CurrentLivingSituation &&
            sdohAssessment.FamilyAndHome.CurrentLivingSituation.Answer != null &&
            sdohAssessment.FamilyAndHome.CurrentLivingSituation.Answer === 'Homeless, No Residence'
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.02',
                Note: 'Unsheltered homelessness',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.FamilyAndHome.HousingSituationToday &&
            sdohAssessment.FamilyAndHome.HousingSituationToday.Answer != null &&
            sdohAssessment.FamilyAndHome.HousingSituationToday.Answer.includes('I do not have housing') &&
            sdohAssessment.FamilyAndHome.CurrentLivingSituation &&
            sdohAssessment.FamilyAndHome.CurrentLivingSituation.Answer != null &&
            (sdohAssessment.FamilyAndHome.CurrentLivingSituation.Answer === 'Homeless, Living with a Friend' ||
                sdohAssessment.FamilyAndHome.CurrentLivingSituation.Answer === 'Homeless, Living in a Shelter')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.01',
                Note: 'Sheltered homelessness',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.FamilyAndHome.WorriedAboutLosingYourHousing &&
            sdohAssessment.FamilyAndHome.WorriedAboutLosingYourHousing.Answer != null &&
            sdohAssessment.FamilyAndHome.WorriedAboutLosingYourHousing.Answer === 'Yes'
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.811',
                Note: 'Housing instability, housed, with risk of homelessness',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.MoneyAndResources.HighestLevelOfSchoolYouFinished &&
            sdohAssessment.MoneyAndResources.HighestLevelOfSchoolYouFinished.Answer != null &&
            sdohAssessment.MoneyAndResources.HighestLevelOfSchoolYouFinished.Answer === 'Less than high school degree'
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z55.5',
                Note: 'Less than a high school diploma',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.MoneyAndResources.CurrentWorkSituation &&
            sdohAssessment.MoneyAndResources.CurrentWorkSituation.Answer != null &&
            sdohAssessment.MoneyAndResources.CurrentWorkSituation.Answer.toLowerCase().includes('unemployed')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z56.0',
                Note: 'Unemployment, unspecified',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (sdohAssessment.MoneyAndResources.TotalIncome && sdohAssessment.MoneyAndResources.TotalIncome.Answer != null) {
            if (
                sdohAssessment.MoneyAndResources.TotalIncome.Answer === 'I have no income' ||
                sdohAssessment.MoneyAndResources.TotalIncome.Answer === 'Less than poverty threshold'
            ) {
                summaryNotes.push({
                    Id: sdohAssessment.Id,
                    CptCode: 'T1016',
                    Zcode: 'Z59.5',
                    Note: 'Extreme poverty',
                    Type: SummaryNoteTypeEnum.SDOH,
                    IsConfirm: true,
                    OrderBy: 3,
                    DisplayName: 'SDOH',
                });
            }
        }

        if (
            sdohAssessment.MoneyAndResources.Food &&
            sdohAssessment.MoneyAndResources.Food.Answer != null &&
            sdohAssessment.MoneyAndResources.Food.Answer === 'Yes'
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.48',
                Note: 'Other specified lack of adequate food',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            (sdohAssessment.MoneyAndResources.Utilities &&
                sdohAssessment.MoneyAndResources.Utilities.Answer != null &&
                sdohAssessment.MoneyAndResources.Utilities.Answer === 'Yes') ||
            (sdohAssessment.MoneyAndResources.Clothing &&
                sdohAssessment.MoneyAndResources.Clothing.Answer != null &&
                sdohAssessment.MoneyAndResources.Clothing.Answer === 'Yes') ||
            sdohAssessment.MoneyAndResources.TotalIncome.Answer === 'Less than $35,000'
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.6',
                Note: 'Low income',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            (sdohAssessment.MoneyAndResources.Phone &&
                sdohAssessment.MoneyAndResources.Phone.Answer != null &&
                sdohAssessment.MoneyAndResources.Phone.Answer === 'Yes') ||
            (sdohAssessment.MoneyAndResources.Medicine &&
                sdohAssessment.MoneyAndResources.Medicine.Answer != null &&
                sdohAssessment.MoneyAndResources.Medicine.Answer === 'Yes') ||
            (sdohAssessment.MoneyAndResources.ChildCare &&
                sdohAssessment.MoneyAndResources.ChildCare.Answer != null &&
                sdohAssessment.MoneyAndResources.ChildCare.Answer === 'Yes')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z59.87',
                Note: 'Material hardship due to limited financial resources, not elsewhere classified',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.SocialAndEmotionalHealth.HowStressedAreYou &&
            sdohAssessment.SocialAndEmotionalHealth.HowStressedAreYou.Answer != null &&
            (sdohAssessment.SocialAndEmotionalHealth.HowStressedAreYou.Answer === 'Quite a bit' ||
                sdohAssessment.SocialAndEmotionalHealth.HowStressedAreYou.Answer === 'Very much')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z63.79',
                Note: 'Other stressful life events affecting family and household',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (
            sdohAssessment.OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner &&
            sdohAssessment.OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.Answer != null &&
            sdohAssessment.OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.Answer.includes('Yes')
        ) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: 'T1016',
                Zcode: 'Z63.0',
                Note: 'Problems in relationship with spouse or partner',
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        if (sdohAssessment.Comment) {
            summaryNotes.push({
                Id: sdohAssessment.Id,
                CptCode: null,
                Zcode: null,
                Note: sdohAssessment.Comment,
                Type: SummaryNoteTypeEnum.SDOH,
                IsConfirm: true,
                OrderBy: 3,
                DisplayName: 'SDOH',
            });
        }
        const summaryNotesWithUniqueZcodes = summaryNotes.filter(
            (note, index, self) =>
                index === self.findIndex((n) => n.Type === note.Type && n.Zcode === note.Zcode && n.CptCode === note.CptCode && n.Note === note.Note)
        );
        let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
        let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, SdohStatus: sdohAssessment.Status };
        const updatedEngagementDraft = {
            ...engagementDraft,
            SummaryNotes: summaryNotesWithUniqueZcodes,
            SdohAssessmentId: sdohAssessment.Id,
        };
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: updatedEngagementDraft,
            MemberAssessmentStatuses: memberAssessmentStatuses,
        };
        return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
    };

export const getLastFootDiabetesAssessment =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        let response = await axios.get(`${API_URL}/assessment/getlastfootdiabetesassessment?memberId=${memberId}`);
        if (response.status !== 200) {
            response = await axios.get(`${API_URL}/assessment/getnewdiabetesfootassessment`);
        }
        dispatch(_setDiabetesFootAssessment(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveFootDiabetesAssessment =
    (diabetesFootAssessment: DiabetesFootAssessment, saveOnlyLocal: boolean = false, canGotoNextMenu: boolean = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        dispatch(_setDiabetesFootAssessment(diabetesFootAssessment));
        try {
            if (!saveOnlyLocal) {
                const isUpdate = Boolean(diabetesFootAssessment.Id);
                const url = `${API_URL}/assessment/${isUpdate ? 'updatediabetesfootassessment' : 'creatediabetesfootassessment'}`;

                const response = await axios.put(url, diabetesFootAssessment);
                if (response.data) {
                    dispatch(_setDiabetesFootAssessment(response.data));
                }

                const engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
                engagementDraft.SummaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.DiabetesFootAssessment);
                engagementDraft.SummaryNotes.push({
                    Id: diabetesFootAssessment.Id || response.data.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: 'Assessed Diabetes Foot Assessment',
                    Type: SummaryNoteTypeEnum.DiabetesFootAssessment,
                    IsConfirm: true,
                    OrderBy: 11,
                    DisplayName: 'Diabetes Foot',
                });
                let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
                let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, DiabetesFootStatus: diabetesFootAssessment.Status };
                const updatedEngagementDraft = {
                    ...engagementDraft,
                    SummaryNotes: engagementDraft.SummaryNotes,
                    DiabetesFootAssessmentId: diabetesFootAssessment.Id || response.data.Id,
                };
                const updatedMemberAssessmentHistory = {
                    ...memberAssessmentHistory,
                    EngagementDraft: updatedEngagementDraft,
                    MemberAssessmentStatuses: memberAssessmentStatuses,
                };
                const isDraftSaved = await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
                if (canGotoNextMenu && isDraftSaved) {
                    dispatch(gotoNextMenu());
                }
                return isDraftSaved;
            }
            if (canGotoNextMenu) {
                dispatch(gotoNextMenu());
            }
            return true;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

export const getMemberAllergies =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/allergies/`, {
            params: { memberId },
        });
        dispatch(_setMemberAllergies(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const toggleShowAlleryModal =
    (show: boolean): AppThunk =>
    (dispatch, getState) => {
        dispatch(_toggleShowAlleryModal(show));
    };

export const saveMemberAllergy =
    (memberAllergy: MemberAllergy): AppThunk =>
    async (dispatch, getState) => {
        memberAllergy.Source = '';
        const allergies = getState().assessment.memberAllergies;
        dispatch(_toggleIsSaving(true));
        const isUpdate = Boolean(memberAllergy.Id);
        const response = isUpdate ? await axios.put(`${API_URL}/allergies/`, memberAllergy) : await axios.post(`${API_URL}/allergies/`, memberAllergy);

        if (isUpdate) {
            dispatch(
                _setMemberAllergies(
                    allergies.map((allergy) => {
                        return allergy.Id === memberAllergy.Id ? memberAllergy : allergy;
                    })
                )
            );
        } else {
            dispatch(_setMemberAllergies([...allergies, response.data]));
        }

        try {
            const veradigmResponse = await axios.put(`${API_URL}/allergies/sendveradigmallergy`, response.data || memberAllergy);
            console.log('veradigmResponse: ', veradigmResponse);
            if (isUpdate) {
                dispatch(
                    _setMemberAllergies(
                        allergies.map((allergy) => {
                            return allergy.Id === memberAllergy.Id ? memberAllergy : allergy;
                        })
                    )
                );
            } else {
                dispatch(_setMemberAllergies([...allergies, veradigmResponse.data]));
            }
            dispatch(_toggleShowAlleryModal(false));
            dispatch(_toggleIsSaving(false));
        } catch (error) {
            console.log(error);
            dispatch(_toggleIsSaving(false));
        }
    };

export const deleteMemberAllergy =
    (allergyId: string): AppThunk =>
    async (dispatch, getState) => {
        const { memberAllergies, deletingAlergyIds } = getState().assessment;
        dispatch(_setDeletingAlergyIds([...deletingAlergyIds, allergyId]));
        await axios.delete(`${API_URL}/allergies/delete`, {
            params: { allergyId },
        });
        dispatch(_setMemberAllergies(memberAllergies.filter((m) => m.Id !== allergyId)));
        dispatch(_setDeletingAlergyIds(deletingAlergyIds.filter((d) => d !== allergyId)));
        dispatch(_toggleIsSaving(false));
    };

export const getCounselingAssessmentById =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/assessment/getcounselingassessmentbyid/`, { params: { id } });
        dispatch(_setCounceling(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveCounselingAssessment =
    (counseling: Counseling): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        const isUpdate = Boolean(counseling.Id);
        const url = isUpdate ? `${API_URL}/assessment/updatecounselingassessment/` : `${API_URL}/assessment/createcounselingassessment/`;

        const response = await axios.put(url, counseling);
        dispatch(_setCounceling(isUpdate ? counseling : response.data));
        dispatch(_toggleIsSaving(false));
        dispatch(gotoNextMenu());
        if (!isUpdate) {
            let engagementDraft = getState().documentEngagement.engagementDraft;
            const updatedEngagementDraft = { ...engagementDraft, CounselingAssessmentId: response.data.Id };
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            const updatedMemberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, CounselingAssessmentStatus: response.data.Status };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
            };
            dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
    };

export const getSuicidalHomicidalIdeationsById =
    (id: string = null): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await (Boolean(id)
            ? axios.get(`${API_URL}/assessment/getsuicidalhomicidalideationsbyid/`, {
                  params: { id },
              })
            : axios.get(`${API_URL}/assessment/getsuicidalhomicidalideations/`));
        dispatch(_setSuicidalHomicidalIdeations(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveSuicidalHomicidalIdeations =
    (suicidalHomicidalIdeations: SuicidalHomicidalIdeations, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_setSuicidalHomicidalIdeations(suicidalHomicidalIdeations));

        dispatch(_toggleIsSaving(true));
        const isUpdate = Boolean(suicidalHomicidalIdeations.Id);
        const url = isUpdate ? `${API_URL}/assessment/updatesuicidalhomicidalideations/` : `${API_URL}/assessment/createsuicidalhomicidalideations/`;

        try {
            const response = await axios.put(url, suicidalHomicidalIdeations);
            if (Boolean(response.data)) {
                dispatch(_setSuicidalHomicidalIdeations(response.data));
            }

            const isDraftSaved = await dispatch(prepareSuicidalHomicidalSummaryNotes(isUpdate ? suicidalHomicidalIdeations : response.data));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        } finally {
            dispatch(_toggleIsSaving(false));
        }
    };

const prepareSuicidalHomicidalSummaryNotes =
    (suicidalHomicidalIdeations: SuicidalHomicidalIdeations): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(suicidalHomicidalIdeations.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.SuicidalHomicidalIdeation);
            summaryNotes.push({
                Id: suicidalHomicidalIdeations.Id,
                CptCode: '',
                Zcode: '',
                Note: `Suicidal/ Homicidal Ideation - ${StatusEnum[suicidalHomicidalIdeations.Status]}`,
                Type: SummaryNoteTypeEnum.SuicidalHomicidalIdeation,
                IsConfirm: true,
                OrderBy: 19,
                DisplayName: 'Suicidal/ Homicidal Ideation',
            });

            const updatedEngagementDraft = {
                ...engagementDraft,
                SummaryNotes: summaryNotes,
                SuicidalHomicidalIdeationId: suicidalHomicidalIdeations.Id,
            };

            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let memberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                SuicidalHomicidalIdeationStatus: suicidalHomicidalIdeations.Status,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: memberAssessmentStatuses,
            };

            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const getMinimentalExamStatusById =
    (id: string = null): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await (Boolean(id)
            ? axios.get(`${API_URL}/assessment/getminimentalexamstatusbyid/`, {
                  params: { id },
              })
            : axios.get(`${API_URL}/assessment/prepareminimentalexamstatus/`));
        dispatch(_setMiniMentalExam(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveMiniMentalExamStatus =
    (miniMentalExam: MiniMentalExam, saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_setMiniMentalExam(miniMentalExam));
        if (!saveLocal) {
            dispatch(_toggleIsSaving(true));
            try {
                const isUpdate = Boolean(miniMentalExam.Id);
                const url = isUpdate ? `${API_URL}/assessment/updateminimentalexamstatus/` : `${API_URL}/assessment/createminimentalexamstatus/`;

                const response = await axios.put(url, miniMentalExam);
                if (Boolean(response.data)) {
                    dispatch(_setMiniMentalExam(response.data));
                }

                const isDraftSaved = await dispatch(prepareMiniMentalSummaryNotes(isUpdate ? miniMentalExam : response.data));
                if (gotoNext && isDraftSaved) {
                    dispatch(gotoNextMenu());
                }
                return isDraftSaved;
            } catch (err) {
                dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
                return false;
            } finally {
                dispatch(_toggleIsSaving(false));
            }
        }
        return false;
    };

const prepareMiniMentalSummaryNotes =
    (miniMentalExam: MiniMentalExam): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(miniMentalExam.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.MiniMentalExam);
            if (Boolean(miniMentalExam.GeneralObservation?.Build?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Build - ${miniMentalExam.GeneralObservation.Build.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.Appearance?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Appearance - ${miniMentalExam.GeneralObservation.Appearance.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.Demeanor?.Types?.length)) {
                let types = [];
                miniMentalExam.GeneralObservation.Demeanor.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let demeanorTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Demeanor - ${demeanorTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.EyeContact?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Eye Contact - ${miniMentalExam.GeneralObservation.EyeContact.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.Behavior?.Types?.length)) {
                let types = [];
                miniMentalExam.GeneralObservation.Behavior.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let behaviorTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Behavior - ${behaviorTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.ActivityLevel?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Activity Level - ${miniMentalExam.GeneralObservation.ActivityLevel.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.GeneralObservation?.Speech?.Types?.length)) {
                let types = [];
                miniMentalExam.GeneralObservation.Speech.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let speechTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Speech - ${speechTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.ThoughtProcess?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.ThoughtProcess.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let thoughtProcessTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Thought Process - ${thoughtProcessTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.Anxiety?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.Anxiety.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let anxietyTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Anxiety - ${anxietyTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.EstimatedIntelligence?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Estimated Intelligence - ${miniMentalExam.Thought.EstimatedIntelligence.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.Delusions?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.Delusions.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let delusionsTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Delusions - ${delusionsTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.DelusionsOther?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.DelusionsOther.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let delusionsTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Delusions (Other) - ${delusionsTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.Hallucinations?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.Hallucinations.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let hallucinationTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Hallucinations - ${hallucinationTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.HallucinationsOther?.Types?.length)) {
                let types = [];
                miniMentalExam.Thought.HallucinationsOther.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let hallucinationTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Hallucinations (Other) - ${hallucinationTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.Perceptions?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Perceptions - ${miniMentalExam.Thought.Perceptions.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.DTS?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `DTS - ${miniMentalExam.Thought.DTS.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Thought?.DTO?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `DTO - ${miniMentalExam.Thought.DTO.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Cognition?.Orientation?.Types?.length)) {
                let types = [];
                miniMentalExam.Cognition.Orientation.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let orientationTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Orientation - ${orientationTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Cognition?.MemoryIssues?.Types?.length)) {
                let types = [];
                miniMentalExam.Cognition.MemoryIssues.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let memoryIssueTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Memory Issues - ${memoryIssueTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Cognition?.AttentionConcentration?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Attention/Concentration - ${miniMentalExam.Cognition.AttentionConcentration.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Cognition?.JudgmentInsight?.Types?.length)) {
                let types = [];
                miniMentalExam.Cognition.JudgmentInsight.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let judgmentInsightTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Judgment/Insight - ${judgmentInsightTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Emotion?.Mood?.Types?.length)) {
                let types = [];
                miniMentalExam.Emotion.Mood.Types.forEach((type) => {
                    types.push(type.Name);
                });
                let moodTypes = types.join(', ');
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Mood - ${moodTypes}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }
            if (Boolean(miniMentalExam.Emotion?.Affect?.Type)) {
                summaryNotes.push({
                    Id: miniMentalExam.Id,
                    CptCode: '',
                    Zcode: '',
                    Note: `Affect - ${miniMentalExam.Emotion.Affect.Type.Name}`,
                    Type: SummaryNoteTypeEnum.MiniMentalExam,
                    IsConfirm: true,
                    OrderBy: 19,
                    DisplayName: 'Mini Mental Exam',
                });
            }

            const updatedEngagementDraft = {
                ...engagementDraft,
                SummaryNotes: summaryNotes,
                MiniMentalExamId: miniMentalExam.Id,
            };

            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, MiniMentalExamStatus: miniMentalExam.Status };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: memberAssessmentStatuses,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const getSubjectiveAssessmentById =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/assessment/getsubjectivesassessmentbyid/`, { params: { id } });
        dispatch(_setSubjectiveAssessment(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveSubjectiveAssessment =
    (subjectiveAssessment: SubjectiveAssessment, gotoNext = false): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        const isUpdate = Boolean(subjectiveAssessment.Id);
        const url = isUpdate ? `${API_URL}/assessment/updatesubjectiveassessment/` : `${API_URL}/assessment/createsubjectiveassessment/`;

        const response = await axios.put(url, subjectiveAssessment);
        dispatch(_setSubjectiveAssessment(isUpdate ? subjectiveAssessment : response.data));
        dispatch(_toggleIsSaving(false));
        if (!isUpdate) {
            const engagementDraft = getState().documentEngagement.engagementDraft;
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, SubjectiveAssessmentStatus: response.data.Status };
            const updatedEngagementDraft = { ...engagementDraft, SubjectiveAssessmentId: response.data.Id };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: memberAssessmentStatuses,
            };
            dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        if (gotoNext) {
            dispatch(gotoNextMenu());
        }
    };

export const getFactorAndRiskAssessmentById =
    (id: string = null): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await (Boolean(id)
            ? axios.get(`${API_URL}/assessment/getfactorsandrisksassessmentbyid/`, { params: { id } })
            : axios.get(`${API_URL}/assessment/preparefactorsandrisksassessment/`));
        dispatch(_setFactorAndRiskAssessment(response.data));
        dispatch(_toggleIsFetching(false));
    };

export const saveFactorAndRiskAssessment =
    (factorAndRiskAssessment: FactorAndRiskAssessment, saveLocal = false, gotoNext = false): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_setFactorAndRiskAssessment(factorAndRiskAssessment));
        if (!saveLocal) {
            dispatch(_toggleIsSaving(true));
            const isUpdate = Boolean(factorAndRiskAssessment.Id);
            const url = isUpdate ? `${API_URL}/assessment/updatefactorandriskassessment/` : `${API_URL}/assessment/createfactorandriskassessment/`;

            const response = await axios.put(url, factorAndRiskAssessment);
            if (Boolean(response.data)) {
                dispatch(_setFactorAndRiskAssessment(response.data));
            }

            dispatch(_toggleIsSaving(false));
            if (!isUpdate) {
                const engagementDraft = getState().documentEngagement.engagementDraft;
                const updatedEngagementDraft = {
                    ...engagementDraft,
                    FactorAndRiskAssessmentId: response.data.Id,
                };
                let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
                let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, FactorAndRiskAssessmentStatus: response.data.Status };
                const updatedMemberAssessmentHistory = {
                    ...memberAssessmentHistory,
                    MemberAssessmentStatuses: memberAssessmentStatuses,
                    EngagementDraft: updatedEngagementDraft,
                };
                dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
            }
            if (gotoNext) {
                dispatch(gotoNextMenu());
            }
        }
    };

export const saveBarrierAndStrengthInCarePlan =
    (factorAndRiskAssessment: FactorAndRiskAssessment, memberId: string): AppThunk =>
    async (dispatch, getState) => {
        let carePlan = new IntegratedCarePlan();
        let barriers = [];
        if (
            factorAndRiskAssessment.LifeContext.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.LifeContext.MemberOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.LifeContext.MemberReports
        )
            barriers.push({ Name: factorAndRiskAssessment.LifeContext.MemberReports });
        if (
            factorAndRiskAssessment.LifeContext.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.LifeContext.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.LifeContext.ProviderAssessed
        )
            barriers.push({ Name: factorAndRiskAssessment.LifeContext.ProviderAssessed });
        if (
            factorAndRiskAssessment.HealthRisk.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.HealthRisk.MemberOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.HealthRisk.MemberReports
        )
            barriers.push({ Name: factorAndRiskAssessment.HealthRisk.MemberReports });
        if (
            factorAndRiskAssessment.HealthRisk.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.HealthRisk.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.HealthRisk.ProviderAssessed
        )
            barriers.push({ Name: factorAndRiskAssessment.HealthRisk.ProviderAssessed });
        if (
            factorAndRiskAssessment.LearningLiteracy.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.LearningLiteracy.MemberOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.LearningLiteracy.MemberReports
        )
            barriers.push({ Name: factorAndRiskAssessment.LearningLiteracy.MemberReports });
        if (
            factorAndRiskAssessment.LearningLiteracy.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.LearningLiteracy.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.LearningLiteracy.ProviderAssessed
        )
            barriers.push({ Name: factorAndRiskAssessment.LearningLiteracy.ProviderAssessed });
        if (
            factorAndRiskAssessment.StrengthOrAsset.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.StrengthOrAsset.MemberOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.StrengthOrAsset.MemberReports
        )
            barriers.push({ Name: factorAndRiskAssessment.StrengthOrAsset.MemberReports });
        if (
            factorAndRiskAssessment.StrengthOrAsset.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.StrengthOrAsset.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Barrier &&
            factorAndRiskAssessment.StrengthOrAsset.ProviderAssessed
        )
            barriers.push({ Name: factorAndRiskAssessment.StrengthOrAsset.ProviderAssessed });

        let strengths = [];
        if (
            factorAndRiskAssessment.LifeContext.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.LifeContext.MemberOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.LifeContext.MemberReports
        )
            strengths.push({ Name: factorAndRiskAssessment.LifeContext.MemberReports });
        if (
            factorAndRiskAssessment.LifeContext.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.LifeContext.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.LifeContext.ProviderAssessed
        )
            strengths.push({ Name: factorAndRiskAssessment.LifeContext.ProviderAssessed });
        if (
            factorAndRiskAssessment.HealthRisk.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.HealthRisk.MemberOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.HealthRisk.MemberReports
        )
            strengths.push({ Name: factorAndRiskAssessment.HealthRisk.MemberReports });
        if (
            factorAndRiskAssessment.HealthRisk.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.HealthRisk.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.HealthRisk.ProviderAssessed
        )
            strengths.push({ Name: factorAndRiskAssessment.HealthRisk.ProviderAssessed });
        if (
            factorAndRiskAssessment.LearningLiteracy.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.LearningLiteracy.MemberOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.LearningLiteracy.MemberReports
        )
            strengths.push({ Name: factorAndRiskAssessment.LearningLiteracy.MemberReports });
        if (
            factorAndRiskAssessment.LearningLiteracy.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.LearningLiteracy.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.LearningLiteracy.ProviderAssessed
        )
            strengths.push({ Name: factorAndRiskAssessment.LearningLiteracy.ProviderAssessed });
        if (
            factorAndRiskAssessment.StrengthOrAsset.MemberReportsIncludedInCarePlan &&
            factorAndRiskAssessment.StrengthOrAsset.MemberOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.StrengthOrAsset.MemberReports
        )
            strengths.push({ Name: factorAndRiskAssessment.StrengthOrAsset.MemberReports });
        if (
            factorAndRiskAssessment.StrengthOrAsset.ProviderAssessedIncludedInCarePlan &&
            factorAndRiskAssessment.StrengthOrAsset.ProviderOption === FactorRiskAssessmentLookupTypeEnum.Strength &&
            factorAndRiskAssessment.StrengthOrAsset.ProviderAssessed
        )
            strengths.push({ Name: factorAndRiskAssessment.StrengthOrAsset.ProviderAssessed });

        delete carePlan.MemberId;
        carePlan.Barriers = barriers;
        carePlan.Strengths = strengths;

        let factorAndRiskAssessmentId = factorAndRiskAssessment.Id;
        axios
            .put(`${API_URL}/careplan/addcareplanfromriskfactorassessment`, carePlan, { params: { memberId, factorAndRiskAssessmentId } })
            .then((response) => {});
    };

/** Missing or year-0001 dates start as today and stay editable, same as a manual assessment. */
const withEditableTestDate = <T extends { TestDate?: string | null; IsTestDateAi?: boolean }>(assessment: T): T => {
    if (!assessment || isDisplayableDate(assessment.TestDate)) {
        return assessment;
    }

    return {
        ...assessment,
        TestDate: new Date().toISOString(),
        IsTestDateAi: false,
    };
};

export const getHgba1cAssessmentById =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/assessment/gethgba1assessmentbyid`, { params: { id } });
        dispatch(_setHgbA1CAssessment(withEditableTestDate(response.data)));
        dispatch(_toggleIsFetching(false));
    };

export const getLastHgba1cAssessment =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        try {
            const response = await axios.get(`${API_URL}/assessment/getlasthgba1assessment`, { params: { memberId } });
            if (response.status !== 200) {
                dispatch(getMemberBasicInfo(memberId));
            } else {
                preparePriorAssessmentForNewDraft(response.data, { clearStatus: true });
                dispatch(_setHgbA1CAssessment(response.data));
                dispatch(_toggleIsFetching(false));
            }
        } catch (error) {
            dispatch(getMemberBasicInfo(memberId));
        }
    };

export const setHgba1cByMemberBasicInfo =
    (memberBasicInfo: MemberBasicInfo): AppThunk =>
    async (dispatch, getState) => {
        const hgbA1C = copyObject(getState().assessment.hgbA1CAssessment);
        hgbA1C.MemberId = memberBasicInfo.Id;
        hgbA1C.MemberName = memberBasicInfo.Name;
        hgbA1C.Age = memberBasicInfo.Age;
        hgbA1C.DOB = memberBasicInfo.DOB;
        if (!isDisplayableDate(hgbA1C.TestDate)) {
            hgbA1C.TestDate = new Date().toISOString();
            hgbA1C.IsTestDateAi = false;
        }
        hgbA1C.TestingMachine = TestingMachines[0];
        dispatch(_setHgbA1CAssessment(hgbA1C));
        dispatch(_toggleIsFetching(false));
    };

export const saveHgbA1CAssessment =
    (hgbA1CAssessment: HgbA1CAssessment, saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_setHgbA1CAssessment(hgbA1CAssessment));
        if (!saveLocal) {
            dispatch(_toggleIsSaving(true));
            const isUpdate = Boolean(hgbA1CAssessment.Id);
            const url = isUpdate ? `${API_URL}/assessment/updatehgba1cassessment/` : `${API_URL}/assessment/createhgba1cassessment/`;

            return axios
                .put(url, hgbA1CAssessment)
                .then(async (response) => {
                    if (Boolean(response.data)) {
                        dispatch(_setHgbA1CAssessment(response.data));
                    }

                    const hgbA1CAId = isUpdate ? hgbA1CAssessment.Id : response.data.Id;

                    let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
                    engagementDraft.SummaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.HgbA1CAssessment);
                    if (hgbA1CAssessment.HgbA1CResult > 0) {
                        let summaryNote = {};
                        if (hgbA1CAssessment.HgbA1CResult < 7) {
                            summaryNote = {
                                Id: hgbA1CAId,
                                CptCode: '3044F',
                                Zcode: '',
                                Note: 'Assessed HgbA1C Assessment',
                                Type: SummaryNoteTypeEnum.HgbA1CAssessment,
                                IsConfirm: true,
                                OrderBy: 10,
                                DisplayName: 'HgbA1C Assessment',
                            };
                        } else if (hgbA1CAssessment.HgbA1CResult >= 7 && hgbA1CAssessment.HgbA1CResult < 8) {
                            summaryNote = {
                                Id: hgbA1CAId,
                                CptCode: '3051F',
                                Zcode: '',
                                Note: 'Assessed HgbA1C Assessment',
                                Type: SummaryNoteTypeEnum.HgbA1CAssessment,
                                IsConfirm: true,
                                OrderBy: 10,
                                DisplayName: 'HgbA1C Assessment',
                            };
                        } else if (hgbA1CAssessment.HgbA1CResult >= 8 && hgbA1CAssessment.HgbA1CResult <= 9) {
                            summaryNote = {
                                Id: hgbA1CAId,
                                CptCode: '3052F',
                                Zcode: '',
                                Note: 'Assessed HgbA1C Assessment',
                                Type: SummaryNoteTypeEnum.HgbA1CAssessment,
                                IsConfirm: true,
                                OrderBy: 10,
                                DisplayName: 'HgbA1C Assessment',
                            };
                        } else if (hgbA1CAssessment.HgbA1CResult > 9) {
                            summaryNote = {
                                Id: hgbA1CAId,
                                CptCode: '3046F',
                                Zcode: '',
                                Note: 'Assessed HgbA1C Assessment',
                                Type: SummaryNoteTypeEnum.HgbA1CAssessment,
                                IsConfirm: true,
                                OrderBy: 10,
                                DisplayName: 'HgbA1C Assessment',
                            };
                        }
                        const summaryNoteForTest = {
                            Id: hgbA1CAId,
                            CptCode: '83037',
                            Zcode: '',
                            Note: 'Assessed HgbA1C POC Test',
                            Type: SummaryNoteTypeEnum.HgbA1CAssessment,
                            IsConfirm: true,
                            OrderBy: 10,
                            DisplayName: 'HgbA1C Assessment POC Test',
                        };
                        engagementDraft.SummaryNotes.push(summaryNoteForTest);
                        engagementDraft.SummaryNotes.push(summaryNote);
                    }
                    engagementDraft.HgbA1CAssessmentId = hgbA1CAId;
                    let memberAssessmentHistory = getState().documentEngagement.memberAssessmentHistory;
                    let updatedMemberAssessmentStatuses = {
                        ...memberAssessmentHistory.MemberAssessmentStatuses,
                        HgbA1CAssessmentStatus: hgbA1CAssessment.Status,
                    };
                    const updatedMemberAssessmentHistory = {
                        ...memberAssessmentHistory,
                        MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                        EngagementDraft: engagementDraft,
                    };
                    const isDraftSaved = await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
                    if (gotoNext && isDraftSaved) {
                        dispatch(gotoNextMenu());
                    }
                    return isDraftSaved;
                })
                .catch((err) => {
                    dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
                    return false;
                })
                .finally(() => {
                    dispatch(_toggleIsSaving(false));
                });
        }
        return false;
    };

export const setAssessmentError =
    (error: MessageAlert): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setError(error));
    };

export const resetAssessmentError = (): AppThunk => (dispatch, getState) => {
    dispatch(_setError(new MessageAlert()));
};

export const setAssessmentSuccess =
    (success: MessageAlert): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setSuccess(success));
    };

export const resetAssessmentSuccess = (): AppThunk => (dispatch, getState) => {
    dispatch(_setSuccess(new MessageAlert()));
};

export const getLipidAssessmentById =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/assessment/getlipidassessmentbyid`, { params: { id } });

        dispatch(_setLipidAssessment(withEditableTestDate(response.data)));
        dispatch(_toggleIsFetching(false));
    };

export const getLastLipidAssessment =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        try {
            const response = await axios.get(`${API_URL}/assessment/getlastlipidassessment`, { params: { memberId } });
            if (response.status !== 200) {
                dispatch(getMemberById(memberId));
            } else {
                const authData = new Authorization();
                dispatch(
                    _setLipidAssessment({
                        MemberId: response.data.MemberId,
                        MemberName: response.data.MemberName,
                        DOB: response.data.DOB,
                        Age: response.data.Age,
                        TestDate: new Date().toISOString(),
                        IsTestDateAi: false,
                        ExpirationDate: response.data.ExpirationDate,
                        Collector: {
                            Id: authData.UserId,
                            Name: authData.UserName,
                            Role: authData.Role,
                        },
                    } as LipidAssessment)
                );
                dispatch(_toggleIsFetching(false));
            }
        } catch (error) {
            dispatch(getMemberBasicInfo(memberId));
        }
    };

export const setLipidAssessmentByMember =
    (member: Member): AppThunk =>
    async (dispatch, getState) => {
        const authData = new Authorization();
        const collector = Boolean(member?.LastUpdatedBy?.Id)
            ? {
                  Id: member.LastUpdatedBy.Id,
                  Name: member.LastUpdatedBy.Name,
              }
            : {
                  Id: authData.UserId,
                  Name: authData.UserName,
                  Role: authData.Role,
              };
        const lipidAssessment = {
            MemberId: member.Id,
            MemberName: `${member.FirstName} ${member.LastName}`,
            Age: member.Age,
            DOB: Boolean(member.DOB) ? new Date(member.DOB).toISOString() : null,
            Collector: collector,
            TestDate: new Date().toISOString(),
            ExpirationDate: new Date().toISOString(),
            Provider: {
                Id: member?.Team?.Id,
                Name: member?.Team?.Name,
                Role: TeamTypes.find((t) => t.Id === member?.Team?.Type)?.Name,
            },
        } as LipidAssessment;
        dispatch(_setLipidAssessment(lipidAssessment));
        dispatch(_toggleIsFetching(false));
    };

export const saveLipidAssessment =
    (lipidAssessment: LipidAssessment, saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_setLipidAssessment(lipidAssessment));
        if (!saveLocal) {
            dispatch(_toggleIsSaving(true));
            const isUpdate = Boolean(lipidAssessment.Id);
            const url = isUpdate ? `${API_URL}/assessment/updatelipidassessment/` : `${API_URL}/assessment/createlipidassessment/`;

            return axios
                .put(url, lipidAssessment)
                .then(async (response) => {
                    if (Boolean(response.data)) {
                        dispatch(
                            _setLipidAssessment({
                                ...lipidAssessment,
                                Id: response.data.Id,
                            })
                        );
                    }

                    const isDraftSaved = await dispatch(prepareLipidSummaryNotes(isUpdate ? lipidAssessment : response.data));
                    if (gotoNext && isDraftSaved) {
                        dispatch(gotoNextMenu());
                    }
                    return isDraftSaved;
                })
                .catch((err) => {
                    dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
                    return false;
                })
                .finally(() => {
                    dispatch(_toggleIsSaving(false));
                });
        }
        return false;
    };

const prepareLipidSummaryNotes =
    (lipidAssessment: LipidAssessment): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(lipidAssessment.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.LipidAssessment);
            summaryNotes.push({
                Id: lipidAssessment.Id,
                CptCode: '80061',
                Zcode: 'Z13.220',
                Note: 'Blood test, lipids (cholesterol and triglycerides)',
                Type: SummaryNoteTypeEnum.LipidAssessment,
                IsConfirm: true,
                OrderBy: 21,
                DisplayName: 'Lipid Assessment',
            });

            const ldlCholestrol = lipidAssessment.LDLCholestrol;
            if (Number.isFinite(ldlCholestrol)) {
                if (ldlCholestrol < 100) {
                    summaryNotes.push({
                        Id: lipidAssessment.Id,
                        CptCode: '3048F',
                        Zcode: '',
                        Note: 'LDL Cholesterol < 100 mg/dl',
                        Type: SummaryNoteTypeEnum.LipidAssessment,
                        IsConfirm: true,
                        OrderBy: 21,
                        DisplayName: 'Lipid Assessment',
                    });
                } else if (ldlCholestrol >= 100 && ldlCholestrol <= 129) {
                    summaryNotes.push({
                        Id: lipidAssessment.Id,
                        CptCode: '3049F',
                        Zcode: '',
                        Note: 'LDL Cholesterol 100-129 mg/dl',
                        Type: SummaryNoteTypeEnum.LipidAssessment,
                        IsConfirm: true,
                        OrderBy: 21,
                        DisplayName: 'Lipid Assessment',
                    });
                } else if (ldlCholestrol >= 130) {
                    summaryNotes.push({
                        Id: lipidAssessment.Id,
                        CptCode: '3050F',
                        Zcode: '',
                        Note: 'LDL Cholesterol >= 130 mg/dl',
                        Type: SummaryNoteTypeEnum.LipidAssessment,
                        IsConfirm: true,
                        OrderBy: 21,
                        DisplayName: 'Lipid Assessment',
                    });
                }
            }

            const updatedEngagementDraft = {
                ...engagementDraft,
                SummaryNotes: summaryNotes,
                LipidAssessmentId: lipidAssessment.Id,
            };
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let memberAssessmentStatuses = { ...memberAssessmentHistory.MemberAssessmentStatuses, LipidAssessmentStatus: lipidAssessment.Status };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: memberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const getCPTCodesForBHDiagnostic = (): AppThunk => async (dispatch, getState) => {
    dispatch(_toggleIsFetching(true));
    const diagnosticCodes = ['90791', '90832', '90834', '90837'];
    const response = await axios.get(`${API_URL}/assessment/getcptcodesforbhdiagnostic`);
    const bhDiagnosticCodes = response.data.Codes.filter((code: any) => diagnosticCodes.includes(code.Code));
    dispatch(_setBHCPTCodeGroup(bhDiagnosticCodes));
    dispatch(_toggleIsFetching(false));
};

export const getFCodesForBHDiagnostic = (): AppThunk => async (dispatch, getState) => {
    dispatch(_toggleIsFetching(true));
    const response = await axios.get(`${API_URL}/assessment/getfcodesforbhdiagnostic`);
    const bhDiagnosticCodes = response.data;
    dispatch(setFCodesForBHDiagnostic(bhDiagnosticCodes));
    dispatch(_toggleIsFetching(false));
};

export const addCompletedAssessmentTimelines =
    (engagementDraft: DocumentEngagement, memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const assessments = [
            {
                status: engagementDraft.AbuseHistoryStatus,
                message: 'Completed Abuse History Assessment.',
            },
            {
                status: engagementDraft.CulturalConsiderationsStatus,
                message: 'Completed Cultural Considerations Assessment.',
            },
            {
                status: engagementDraft.CurrentNeedsStatus,
                message: 'Completed Current Needs Assessment.',
            },
            {
                status: engagementDraft.DevelopmentalHistoryStatus,
                message: 'Completed Developmental History Assessment.',
            },
            {
                status: engagementDraft.EatingDisorderStatus,
                message: 'Completed Eating Disorder Assessment.',
            },
            {
                status: engagementDraft.GAFScaleStatus,
                message: 'Completed GAF Scale Assessment.',
            },
            {
                status: engagementDraft.MentalHealthTreatmentHistoryStatus,
                message: 'Completed Mental Health Treatment History Assessment.',
            },
            {
                status: engagementDraft.MiniMentalExamStatus,
                message: 'Provider has completed Mini Mental Exam Assessment.',
            },
            {
                status: engagementDraft.SdohAssessmentStatus,
                message: 'Provider has completed SDOH Assessment.',
            },
            {
                status: engagementDraft.TobaccoAndDrugAssessmentStatus,
                message: 'Provider has completed Tobacco and Drug Assessment.',
            },
        ];

        // Insert timeline entries for completed assessments
        assessments.forEach((assessment) => {
            if (assessment.status === StatusEnum.Completed) {
                dispatch(insertTimeline(memberId, assessment.message, TimelineTypeEnum.Assessments));
            }
        });
    };

export const updateAssessmentMenu =
    (prepareMenuClass: PrepareMenuClass): AppThunk =>
    (dispatch, getState) => {
        const allRoles = getState().assessment.roles;
        const assessment = getState().assessment;
        const hra = getState().hra;
        const fuhfum = getState().fuhfum;
        const member = getState().memberDetail.memberBasicInfo;
        const resolvedEngagementDraft =
            (prepareMenuClass.EngagementDraft as DocumentEngagement | undefined) ??
            (getState().documentEngagement.engagementDraft as DocumentEngagement | undefined);
        const engagementDraft = resolvedEngagementDraft ?? new DocumentEngagement();
        const memberAssessmentHistory = prepareMenuClass.MemberAssessmentHistory ?? getState().documentEngagement.memberAssessmentHistory;
        const role = prepareMenuClass.Role ?? getState().assessment.selectedRole;
        const activeMenu = prepareMenuClass.ActiveMenu ?? getState().assessment.assessmentMenuList.find((a) => a.IsActive)?.Name;
        let status: any;
        const selectedRoleObject = allRoles.find((r) => r.Name === role);
        let menuList = selectedRoleObject?.AssignedAssessments || [];
        menuList = [...menuList].sort((a, b) => (a.Order || 0) - (b.Order || 0));
        if (member?.IsInFuhFum && role === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST) {
            const bhsFUHFUM = allRoles.find((r) => r.Name === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST_FUHFUM);
            menuList = [...(bhsFUHFUM?.AssignedAssessments || [])].sort((a, b) => (a.Order || 0) - (b.Order || 0));
        }

        if (member?.IsInFuhFum && role === roleEnum.NURSE_PRACTITIONER) {
            const npFUHFUM = allRoles.find((r) => r.Name === roleEnum.NURSE_PRACTITIONER_FUHFUM);
            menuList = [...(npFUHFUM?.AssignedAssessments || [])].sort((a, b) => (a.Order || 0) - (b.Order || 0));
        }

        // Safety Plan is Heidi-trackable / Care Plan data — no Assessment.tsx panel (avoids a blank tab).
        menuList = menuList.filter((a) => a?.Name !== assessmentMenuEnum.SAFETY_PLAN);

        const assessmentMenuList = menuList.map((menuName, index) => {
            return {
                Id: index + 1,
                Name: menuName.Name,
                IsActive: activeMenu === menuName.Name,
                IsPending: false,
                IsCompleted: false,
                IsDisabled: Boolean(index) && !Boolean(engagementDraft.ConsentFormId),
                IsExpired: false,
                IsActiveIconShow: true,
                IsNeedsReview: false,
            } as MenuDetails;
        });
        console.log('assessmentMenuList: ', assessmentMenuList);
        assessmentMenuList.forEach((menu) => {
            switch (menu.Name) {
                case assessmentMenuEnum.ADVANCED_DIRECTIVE:
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.AdvancedDirectiveStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.ALLERGIES:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.AllergyStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.AllergyStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.ADL:
                    const { adlAssessment } = hra;
                    status = Boolean(adlAssessment?.Status) ? adlAssessment.Status : memberAssessmentHistory?.MemberAssessmentStatuses.AdlAssessmentStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.BUDGET_FORM:
                    menu.IsCompleted = Boolean(engagementDraft.BudgetFormId);
                    break;
                case assessmentMenuEnum.CARE_CIRCLE:
                    menu.IsCompleted = engagementDraft.CareCircleConfirmation?.CareCircleStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.CARE_PLANS:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.CarePlanStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.CarePlanStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.CONSENT:
                    const consentData = assessment?.consentDocumentValid;
                    if (consentData?.length > 0 && consentData?.some((c) => c.IsExpired)) {
                        menu.IsExpired = true;
                        menu.IsCompleted = false;
                    } else if (Boolean(engagementDraft.ConsentFormId)) {
                        menu.IsCompleted = true;
                    } else if (consentData?.length === 0 && Boolean(memberAssessmentHistory) && memberAssessmentHistory.ShowConsentFormAlert) {
                        menu.IsExpired = true;
                        menu.IsCompleted = false;
                    }
                    break;
                case assessmentMenuEnum.COUNSELING:
                    status = Boolean(assessment?.counseling?.Status)
                        ? assessment?.counseling.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.CounselingStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.DEMOGRAPHICS:
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.DemographicStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.DIABETES_FOOT:
                    status = Boolean(assessment?.diabetesFootAssessment?.Status)
                        ? assessment?.diabetesFootAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.DiabetesFootStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.EDUCATION:
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.EducationStatus === StatusEnum.Completed;
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.EducationStatus === StatusEnum.InProgress;
                    break;
                case assessmentMenuEnum.FALL_RISK:
                    const { fallRisk } = hra;
                    status = Boolean(fallRisk?.Status) ? fallRisk.Status : memberAssessmentHistory?.MemberAssessmentStatuses.FallRiskStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.SCHEDULES:
                case assessmentMenuEnum.FOLLOW_UP:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.FollowUpStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.FollowUpStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.HEALTH_PLAN:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.HealthPlanStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.HealthPlanStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.HRA_EDUCATION:
                    const { education } = hra;
                    status = Boolean(education?.Status) ? education.Status : memberAssessmentHistory?.MemberAssessmentStatuses.HraEducationStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    menu.DisplayName = 'Education';
                    break;
                case assessmentMenuEnum.HGBA1C_ASSESSMENT:
                    status = Boolean(assessment?.hgbA1CAssessment?.Status)
                        ? assessment?.hgbA1CAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.HgbA1CStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.INTERVENTIONS:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.InterventionStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.InterventionStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.LIPID_ASSESSMENT:
                    status = Boolean(assessment?.lipidAssessment?.Status)
                        ? assessment?.lipidAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.LipidStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.MEDICATION_RECONCILIATION:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.MedicationReconciliationStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.MedicationReconciliationStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.MINI_MENTAL_STATUS_EXAM:
                    status = Boolean(assessment?.miniMentalExam?.Status)
                        ? assessment.miniMentalExam.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.MiniMentalExamStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.OTHER_FACTORS_AND_RISKS:
                    status = Boolean(assessment?.factorAndRiskAssessment?.Status)
                        ? assessment.factorAndRiskAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.FactorAndRiskAssessmentStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PAST_PRESENT_HISTORY:
                    const pastPresentAssessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PastPresentHistory
                    );
                    status = Boolean(pastPresentAssessment)
                        ? pastPresentAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PastPresentHistoryStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PAST_SURGICAL_HISTORY:
                    const pastSurgicalAssessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PastSurgicalHistory
                    );
                    status = Boolean(pastSurgicalAssessment)
                        ? pastSurgicalAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PastSurgicalHistoryStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PCP_SPECIALIST:
                    menu.IsPending = memberAssessmentHistory.MemberAssessmentStatuses.PcpSpecialistStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory.MemberAssessmentStatuses.PcpSpecialistStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PHQ9:
                    const phq9Assessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PatientHealthQuestionnaire
                    );
                    status = Boolean(phq9Assessment) ? phq9Assessment.Status : memberAssessmentHistory?.MemberAssessmentStatuses.PhqStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    menu.IsExpired = status !== StatusEnum.Completed && memberAssessmentHistory?.ShowPhqAlert;
                    break;
                case assessmentMenuEnum.PHYSICAL_EXAM:
                    const physicalExamAssessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PhysicalExam
                    );
                    status = Boolean(physicalExamAssessment)
                        ? physicalExamAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PhysicalExamStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PHYSICAL_HEALTH:
                    // Physical Health is complete if ANY of the 4 sub-assessments are completed
                    const pastPresentPhysicalHealth = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PastPresentHistory
                    );
                    const pastSurgicalPhysicalHealth = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PastSurgicalHistory
                    );
                    const reviewSystemsPhysicalHealth = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.ReviewSystems
                    );
                    const physicalExamPhysicalHealth = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.PhysicalExam
                    );

                    const pastPresentStatus = Boolean(pastPresentPhysicalHealth)
                        ? pastPresentPhysicalHealth.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PastPresentHistoryStatus;
                    const pastSurgicalStatus = Boolean(pastSurgicalPhysicalHealth)
                        ? pastSurgicalPhysicalHealth.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PastSurgicalHistoryStatus;
                    const reviewSystemsStatus = Boolean(reviewSystemsPhysicalHealth)
                        ? reviewSystemsPhysicalHealth.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.ReviewSystemStatus;
                    const physicalExamStatus = Boolean(physicalExamPhysicalHealth)
                        ? physicalExamPhysicalHealth.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PhysicalExamStatus;

                    menu.IsCompleted =
                        pastPresentStatus === StatusEnum.Completed ||
                        pastSurgicalStatus === StatusEnum.Completed ||
                        reviewSystemsStatus === StatusEnum.Completed ||
                        physicalExamStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PRAPARE_SDOH:
                    status = Boolean(assessment.sdohAssessment?.Status)
                        ? assessment.sdohAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.SdohAssessmentStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PREVENTATION_HEALTH_ADVICE:
                    const { preventativeHealthAdvice } = hra;
                    status = Boolean(preventativeHealthAdvice?.Status)
                        ? preventativeHealthAdvice.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.PreventativeHealthAdviceStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.ADDITIONAL_CHP_CRITERIA:
                    const { additionalCHPCriteria } = hra;
                    status = Boolean(additionalCHPCriteria?.Status)
                        ? additionalCHPCriteria.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.AdditionalCHPCriteriaStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.PROCEDURE_SIGN_OFF:
                    menu.IsDisabled = !Boolean(engagementDraft.VisitedTime) || (role !== roleEnum.HRA && !Boolean(engagementDraft.ReasonForVisit?.Id));
                    break;
                case assessmentMenuEnum.PROGRESS_NOTES:
                    menu.IsCompleted = Boolean(memberAssessmentHistory?.MemberAssessmentStatuses.ProgressNoteStatus);
                    break;
                case assessmentMenuEnum.REMOTE_MONITORING:
                    menu.IsCompleted = Boolean(memberAssessmentHistory?.MemberAssessmentStatuses.RemoteMonitoringStatus);
                    break;
                case assessmentMenuEnum.REQUESTS_REFERRALS:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.RequestStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.RequestStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.REVIEW_SYSTEMS:
                    const reviewSystemAssessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.ReviewSystems
                    );
                    status = Boolean(reviewSystemAssessment)
                        ? reviewSystemAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.ReviewSystemStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.SET_APPOINTMENTS:
                    // menu.IsCompleted = Boolean(engagementDraft.VisitedTime);
                    menu.IsCompleted = Boolean(engagementDraft.AssociatedAppointmentId !== '' && engagementDraft.AssociatedAppointmentId !== null);
                    break;
                case assessmentMenuEnum.SEXUAL_HEALTH:
                    const { sexualHealth } = hra;
                    status = Boolean(sexualHealth?.Status) ? sexualHealth.Status : memberAssessmentHistory?.MemberAssessmentStatuses.SexualHealthStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    const memberBasicInfo = getState().memberDetail.memberBasicInfo;
                    menu.DisplayName =
                        memberBasicInfo.Gender !== GenderEnum.MaleDescription && memberBasicInfo.Gender !== GenderEnum.FemaleDescription
                            ? `${GenderEnum.MaleDescription}/${GenderEnum.FemaleDescription} Health`
                            : `${memberBasicInfo.Gender} Health`;
                    break;
                case assessmentMenuEnum.SUBJECTIVE_ASSESSMENT:
                    status = Boolean(assessment?.subjectiveAssessment?.Status)
                        ? assessment.subjectiveAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.SubjectiveAssessmentStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.SUMMARY_NOTES:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.DocumentEngagementStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.DocumentEngagementStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.TABLETS:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.TabletStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.TabletStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.TRANSITIONAL_CARE:
                    menu.IsExpired = memberAssessmentHistory?.EngagementDraft?.IsInTransition;
                    menu.IsCompleted = memberAssessmentHistory?.EngagementDraft?.DraftWorkflowState?.TransitionalCareStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.TOBACCO_AND_DRUG:
                    const { tobaccoAndDrug } = hra;
                    status = Boolean(tobaccoAndDrug?.Status)
                        ? tobaccoAndDrug.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.TobaccoAndDrugAssessmentStatus;
                    menu.IsExpired = status === StatusEnum.Expired;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.URINALYSIS_REPORT:
                    const urinalysisAssessment = assessment.memberAssessmentsByType.find(
                        (m) => m.Assessment.AssessmentType === AssessmentTypeEnum.UrinalysisReport
                    );
                    status = Boolean(urinalysisAssessment)
                        ? urinalysisAssessment.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.UrinalysisReportStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.VISION_AND_HEARING:
                    const { visionAndHearing } = hra;
                    status = Boolean(visionAndHearing?.Status)
                        ? visionAndHearing.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.VisionAndHearingStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.VITAL_SIGNS:
                    status = Boolean(assessment?.vitalSign?.AssessmentStatus)
                        ? assessment.vitalSign.AssessmentStatus
                        : engagementDraft?.VitalSignStatus
                        ? engagementDraft.VitalSignStatus
                        : memberAssessmentHistory?.MemberAssessmentStatuses.VitalSignStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.SCREENING_FUHFUM:
                    const { screeningFuhFum } = fuhfum;
                    status = Boolean(screeningFuhFum?.Status)
                        ? screeningFuhFum.Status
                        : memberAssessmentHistory?.MemberAssessmentStatuses.ScreeningFuhfumStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.HAMILTON_ANXIETY_SCALE:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.HamiltonAnxietyScaleStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.HamiltonAnxietyScaleStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.CULTURAL_CONSIDERATIONS:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.CulturalConsiderationsStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.CulturalConsiderationsStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.DEVELOPMENTAL_HISTORY:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.DevelopmentalHistoryStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.DevelopmentalHistoryStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.EATING_DISORDER:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.EatingDisorderStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.EatingDisorderStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.LEGAL_HISTORY:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.LegalHistoryStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.LegalHistoryStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.ABUSE_HISTORY:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.AbuseHistoryStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.AbuseHistoryStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.MENTAL_HEALTH_TREATMENT_HISTORY:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.MentalHealthTreatmentHistoryStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.MentalHealthTreatmentHistoryStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.CURRENT_NEEDS:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.CurrentNeedsStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.CurrentNeedsStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.GAF_SCALE:
                    menu.IsPending = memberAssessmentHistory?.MemberAssessmentStatuses.GAFScaleStatus === StatusEnum.InProgress;
                    menu.IsCompleted = memberAssessmentHistory?.MemberAssessmentStatuses.GAFScaleStatus === StatusEnum.Completed;
                    break;
                case assessmentMenuEnum.SUICIDAL_HOMICIDAL_IDEATION:
                    const suicidalStatus = assessment?.suicidalHomicidalIdeations?.Status;
                    status =
                        suicidalStatus !== null && suicidalStatus !== undefined
                            ? suicidalStatus
                            : memberAssessmentHistory?.MemberAssessmentStatuses.SuicidalHomicidalIdeationStatus;
                    menu.IsPending = status === StatusEnum.InProgress;
                    menu.IsCompleted = status === StatusEnum.Completed;
                    break;
            }
        });
        if (resolvedEngagementDraft && activeMenu !== assessmentMenuEnum.CONSENT) {
            const copyEngagementDraft = copyObject(engagementDraft);
            copyEngagementDraft.CurrentMenu = activeMenu;
            dispatch(setEngagementDraft(copyEngagementDraft));
        }

        dispatch(_updateSelectedRole(role));
        dispatch(_updateAssessmentMenuList(assessmentMenuList));
    };

export default assessmentSlice.reducer;
